import { useIsFocused } from 'expo-router/react-navigation';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  AWAY_CALENDAR_TOUR_COPY,
  AWAY_CALENDAR_TOUR_KEY,
  AWAY_CALENDAR_TOUR_TEXT,
  AWAY_CALENDAR_TOUR_ZONE,
  canStartAwayCalendarTour,
  createAwayCalendarTourOutcomes,
} from './awayCalendarTourModel';

import { TourTooltipCard } from '@/components/TourTooltipCard';
import {
  SpotlightTourProvider,
  SpotlightZone,
  useSpotlightTour,
  type SpotlightStep,
} from '@/components/spotlight/SpotlightTour';
import { View } from '@/components/ui/view';
import { DURATION } from '@/constants/animation';
import { useColors } from '@/hooks/useColors';
import { useTourCallout } from '@/hooks/useTourCallout';
import { CALLOUT_KEYS } from '@/services/userCallouts';

/**
 * How long the tour waits after the screen is ready before it shows, so the
 * screen's transition and the list's first layout have settled.
 */
const START_DELAY = DURATION.slow + DURATION.fast;

/**
 * How long the spotlight waits after the Availability row is brought on
 * screen, so the scroll has landed when it measures the row.
 */
const REVEAL_SETTLE_DELAY = 100;

/** Room the highlight leaves around the row's content, wider at the sides to match the card's own inner padding. */
const HIGHLIGHT_PADDING = { x: 12, y: 8 };

/** The Account list's side gutter, its `px-5`. */
const LIST_GUTTER = 20;

/** The card's distance from the screen edges, so its sides line up with the highlight's. */
const CARD_GUTTER = LIST_GUTTER - HIGHLIGHT_PADDING.x;

/** Rounds the highlight's corners to match the card's `rounded-2xl`, so the row and the card read as one pair. */
const HIGHLIGHT_RADIUS = 16;

type Outcomes = ReturnType<typeof createAwayCalendarTourOutcomes>;

type TourContextValue = {
  /** Whether the Availability row is registered as the tour's target. */
  isZoneActive: boolean;
  /** Whether the tour is on screen now, for drawing what only shows during it. */
  isShowing: boolean;
};

const AwayCalendarTourContext = createContext<TourContextValue>({
  isZoneActive: false,
  isShowing: false,
});

/**
 * The card beside the Availability row. One stop, so one button: a second
 * that closes it the same way would be a choice with nothing to choose. A tap
 * on the scrim still dismisses it.
 */
function AwayCalendarTourTooltip({
  step,
  outcomes,
  onStop,
}: {
  step: SpotlightStep;
  outcomes: Outcomes;
  onStop: () => void;
}) {
  return (
    <TourTooltipCard
      pointer={step.placement === 'below' ? 'up' : 'down'}
      textRef={step.focusRef}
      text={AWAY_CALENDAR_TOUR_TEXT}
      primaryLabel={AWAY_CALENDAR_TOUR_COPY.done}
      onPrimary={() => {
        outcomes.resolve('completed');

        onStop();

        step.stop();
      }}
    />
  );
}

type DriverProps = {
  canStart: boolean;
  /** Brings the Availability row on screen, so the tour has a row to point at. */
  reveal: () => void;
  /** The sheet the highlighted row opens is open. */
  isStopOpen: boolean;
  isShowingRef: { current: boolean };
  outcomes: Outcomes;
  onStop: () => void;
};

/**
 * Starts the tour once it may, and takes it off the screen when it may not,
 * as when a sheet opens or the expert leaves Account. A start made before the
 * Availability row has rendered waits for it.
 *
 * The row is revealed when the start delay ends, and the spotlight starts a
 * moment later, so the row has stopped moving when it is measured. The
 * spotlight measures once and never scrolls, so a row below the fold would
 * otherwise be drawn off screen. A reveal any earlier lands before the list has
 * laid out and scrolls nowhere.
 *
 * A tap on the highlighted row reaches the row and opens its sheet, which
 * covers the tour. That is the expert taking the tour up, so it counts as
 * completed. Anything else covering the tour records nothing, so it comes back.
 */
function AwayCalendarTourDriver({
  canStart,
  reveal,
  isStopOpen,
  isShowingRef,
  outcomes,
  onStop,
}: DriverProps) {
  const { start, stop } = useSpotlightTour(AWAY_CALENDAR_TOUR_KEY);

  const isStopOpenRef = useRef(isStopOpen);

  // A layout effect, because the start effect's cleanup reads it in the same
  // commit, and layout effects run before passive cleanups.
  useLayoutEffect(() => {
    isStopOpenRef.current = isStopOpen;
  });

  useEffect(() => {
    if (!canStart) {
      return;
    }

    let hasStarted = false;

    let startTimer: ReturnType<typeof setTimeout> | undefined;

    const revealTimer = setTimeout(() => {
      reveal();

      startTimer = setTimeout(() => {
        hasStarted = true;

        start();
      }, REVEAL_SETTLE_DELAY);
    }, START_DELAY);

    return () => {
      clearTimeout(revealTimer);

      clearTimeout(startTimer);

      if (hasStarted) {
        if (isShowingRef.current && isStopOpenRef.current) {
          outcomes.resolve('completed');
        }

        onStop();

        stop();
      }
    };
  }, [canStart, reveal, start, stop, isShowingRef, outcomes, onStop]);

  return null;
}

type AwayCalendarTourProviderProps = {
  /** An expert who can see the calendar, with their account loaded. */
  isEligible: boolean;
  /** A modal, sheet or form of the screen is open. */
  isCovered: boolean;
  /** The sheet the Availability row opens is open, which means the expert took up the tour. */
  isStopOpen: boolean;
  /**
   * Brings the Availability row to a place the tour can point at. Called once
   * the start delay ends, just before the tour starts.
   */
  revealStop?: () => void;
  /** Classes for the column the screen's content sits in. */
  className?: string;
  children: ReactNode;
};

/**
 * Hosts the away calendar tutorial for the Account screen. It starts the first
 * time an eligible expert opens Account, gated by a server-backed callout so it
 * shows once per expert across devices, and never while anything covers the
 * screen or before the account and callouts have loaded.
 *
 * The spotlight dims the whole window, the native header included. A tap on
 * the scrim counts as a dismissal; leaving the screen mid-tour counts as
 * nothing, so the tour comes back next time.
 *
 * Dark mode dims with black at a heavier alpha. The light theme's scrim is
 * itself a near-black, and over an already dark screen it barely darkens.
 */
export function AwayCalendarTourProvider({
  isEligible,
  isCovered,
  isStopOpen,
  revealStop,
  className,
  children,
}: AwayCalendarTourProviderProps) {
  const { colors, isDarkScheme } = useColors();

  const revealStopRef = useRef(revealStop);

  useEffect(() => {
    revealStopRef.current = revealStop;
  });

  // Stable, so a screen that passes a new function each render cannot restart the start timer.
  const [reveal] = useState(() => () => revealStopRef.current?.());

  const isFocused = useIsFocused();

  const callout = useTourCallout({
    key: CALLOUT_KEYS.EXPERT_AWAY_CALENDAR_INTRO,
    enabled: isEligible,
  });

  const calloutRef = useRef(callout);

  useEffect(() => {
    calloutRef.current = callout;
  });

  const [outcomes] = useState(() => createAwayCalendarTourOutcomes(calloutRef));

  const canStart = canStartAwayCalendarTour({
    isEligible,
    shouldShowCallout: callout.shouldShow,
    isFocused,
    isCovered,
  });

  const isShowingRef = useRef(false);

  const [isShowing, setIsShowing] = useState(false);

  const [markShowing] = useState(() => (showing: boolean) => {
    isShowingRef.current = showing;

    setIsShowing(showing);
  });

  const [onStop] = useState(() => () => markShowing(false));

  const isZoneActive = isEligible && callout.shouldShow;

  const contextValue = useMemo(() => ({ isZoneActive, isShowing }), [isZoneActive, isShowing]);

  return (
    <View className="h-full w-full flex-1">
      <AwayCalendarTourContext.Provider value={contextValue}>
        <SpotlightTourProvider
          renderCard={(step) => (
            <AwayCalendarTourTooltip step={step} outcomes={outcomes} onStop={onStop} />
          )}
          backdropColor={isDarkScheme ? `${colors.black}CC` : `${colors.background.dark}99`}
          cardGutter={CARD_GUTTER}
          onDismiss={(step) => {
            outcomes.resolve('dismissed');

            onStop();

            step.stop();
          }}
          onStepShown={() => {
            markShowing(true);

            outcomes.shown();
          }}>
          <AwayCalendarTourDriver
            canStart={canStart}
            reveal={reveal}
            isStopOpen={isStopOpen}
            isShowingRef={isShowingRef}
            outcomes={outcomes}
            onStop={onStop}
          />

          <View className={className}>{children}</View>
        </SpotlightTourProvider>
      </AwayCalendarTourContext.Provider>
    </View>
  );
}

/**
 * Marks the Availability row as the tour's one stop. In dark mode the row is
 * barely lighter than the scrim, so while the tour shows it takes the card's
 * `outline-300` edge, drawn over the padded highlight so it lines up with the
 * cutout.
 */
export function AwayCalendarTourZone({ children }: { children: ReactNode }) {
  const { isZoneActive, isShowing } = useContext(AwayCalendarTourContext);

  const { isDarkScheme } = useColors();

  return (
    <SpotlightZone
      tourKey={AWAY_CALENDAR_TOUR_KEY}
      zone={AWAY_CALENDAR_TOUR_ZONE}
      active={isZoneActive}
      radius={HIGHLIGHT_RADIUS}
      padding={HIGHLIGHT_PADDING}>
      <View>
        {children}

        {isShowing && isDarkScheme ? (
          <View
            pointerEvents="none"
            className="absolute rounded-2xl border border-outline-300"
            style={{
              top: -HIGHLIGHT_PADDING.y,
              bottom: -HIGHLIGHT_PADDING.y,
              left: -HIGHLIGHT_PADDING.x,
              right: -HIGHLIGHT_PADDING.x,
            }}
            testID="away-tour-highlight-edge"
          />
        ) : null}
      </View>
    </SpotlightZone>
  );
}
